// Chemin: C:\gestion-copro\src\types\log.types.ts
// Types pour la traçabilité des actions - Copro Protect
// Session 40 : création initiale - module Communications

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// TYPES D'ACTIONS
// ============================================================================

/**
 * Catégories d'actions traçables
 */
export type CategorieLog = 
  | 'onboarding'      // Création copro, invitations, confirmations
  | 'courrier'        // Rédaction, soumission, vote, envoi
  | 'email'           // Réception, archivage, analyse
  | 'document'        // Upload, suppression, classification
  | 'affaire'         // Création, modification, clôture
  | 'signalement'     // Création, traitement, clôture
  | 'utilisateur'     // Connexion, modification profil, changement rôle
  | 'systeme';        // Actions automatiques (notifications, etc.)

/**
 * Actions spécifiques traçables
 */
export type ActionLog = 
  // Onboarding
  | 'copropriete_creee'
  | 'invitation_envoyee'
  | 'confirmation_admin_recue'
  | 'copropriete_activee'
  | 'copropriete_suspendue'
  
  // Courrier
  | 'courrier_cree'
  | 'courrier_modifie'
  | 'courrier_soumis'
  | 'courrier_vote_approuve'
  | 'courrier_vote_modification'
  | 'courrier_vote_rejete'
  | 'courrier_valide'
  | 'courrier_rejete'
  | 'courrier_envoye_email'
  | 'courrier_envoye_postal'
  | 'courrier_annule'
  | 'courrier_duplique'
  
  // Email
  | 'email_recu'
  | 'email_archive'
  | 'email_analyse_sentinelle'
  | 'email_pj_extraite'
  
  // Document
  | 'document_uploade'
  | 'document_supprime'
  | 'document_classifie'
  | 'document_analyse'
  
  // Affaire
  | 'affaire_creee'
  | 'affaire_modifiee'
  | 'affaire_cloturee'
  | 'affaire_rouverte'
  
  // Signalement
  | 'signalement_cree'
  | 'signalement_traite'
  | 'signalement_cloture'
  
  // Utilisateur
  | 'utilisateur_connecte'
  | 'utilisateur_deconnecte'
  | 'utilisateur_profil_modifie'
  | 'utilisateur_role_modifie'
  | 'utilisateur_invite'
  | 'utilisateur_active'
  | 'utilisateur_desactive'
  
  // Système
  | 'notification_envoyee'
  | 'rappel_vote_envoye'
  | 'backup_effectue'
  | 'erreur_systeme';

/**
 * Niveau d'importance du log
 */
export type NiveauLog = 'info' | 'important' | 'critique';

// ============================================================================
// ENTITÉ PRINCIPALE
// ============================================================================

/**
 * Entrée de log (traçabilité)
 * Document Firestore dans la collection 'logs'
 */
export interface Log {
  id: string;
  coproprieteId: string;
  
  // --- Classification ---
  
  categorie: CategorieLog;
  action: ActionLog;
  niveau: NiveauLog;
  
  // --- Acteur ---
  
  /**
   * ID de l'utilisateur qui a effectué l'action
   * Null si action système automatique
   */
  acteurId: string | null;
  
  /**
   * Nom affiché de l'acteur (pour historique lisible)
   */
  acteurNom: string;
  
  /**
   * Adresse IP de l'acteur (pour preuves légales)
   */
  acteurIp?: string;
  
  // --- Cible ---
  
  /**
   * Type d'entité concernée par l'action
   */
  cibleType?: 'courrier' | 'email' | 'document' | 'affaire' | 'signalement' | 'utilisateur' | 'copropriete';
  
  /**
   * ID de l'entité concernée
   */
  cibleId?: string;
  
  /**
   * Libellé de la cible (pour historique lisible)
   */
  cibleLibelle?: string;
  
  // --- Détails ---
  
  /**
   * Description lisible de l'action
   */
  description: string;
  
  /**
   * Données supplémentaires (JSON libre)
   * Ex: ancien/nouveau statut, commentaire de vote, etc.
   */
  details?: Record<string, unknown>;
  
  // --- Timestamp ---
  
  date: Timestamp;
}

// ============================================================================
// DONNÉES POUR CRÉATION
// ============================================================================

/**
 * Données pour créer une entrée de log
 */
export interface LogCreateData {
  coproprieteId: string;
  categorie: CategorieLog;
  action: ActionLog;
  niveau?: NiveauLog;           // Défaut: 'info'
  acteurId: string | null;
  acteurNom: string;
  acteurIp?: string;
  cibleType?: Log['cibleType'];
  cibleId?: string;
  cibleLibelle?: string;
  description: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// LABELS ET CONSTANTES
// ============================================================================

export const CATEGORIE_LOG_LABELS: Record<CategorieLog, string> = {
  onboarding: 'Onboarding',
  courrier: 'Courrier',
  email: 'Email',
  document: 'Document',
  affaire: 'Affaire',
  signalement: 'Signalement',
  utilisateur: 'Utilisateur',
  systeme: 'Système'
};

export const CATEGORIE_LOG_ICONS: Record<CategorieLog, string> = {
  onboarding: 'UserPlus',
  courrier: 'Mail',
  email: 'Inbox',
  document: 'FileText',
  affaire: 'Briefcase',
  signalement: 'AlertTriangle',
  utilisateur: 'User',
  systeme: 'Settings'
};

export const NIVEAU_LOG_LABELS: Record<NiveauLog, string> = {
  info: 'Information',
  important: 'Important',
  critique: 'Critique'
};

export const NIVEAU_LOG_COLORS: Record<NiveauLog, string> = {
  info: 'bg-gray-100 text-gray-800',
  important: 'bg-blue-100 text-blue-800',
  critique: 'bg-red-100 text-red-800'
};

/**
 * Descriptions lisibles des actions
 * Utilisé pour générer automatiquement la description du log
 */
export const ACTION_LOG_DESCRIPTIONS: Record<ActionLog, string> = {
  // Onboarding
  copropriete_creee: 'Copropriété créée',
  invitation_envoyee: 'Invitation envoyée',
  confirmation_admin_recue: 'Confirmation de désignation reçue',
  copropriete_activee: 'Copropriété activée',
  copropriete_suspendue: 'Copropriété suspendue',
  
  // Courrier
  courrier_cree: 'Courrier créé',
  courrier_modifie: 'Courrier modifié',
  courrier_soumis: 'Courrier soumis à validation',
  courrier_vote_approuve: 'Vote : approuvé',
  courrier_vote_modification: 'Vote : modification demandée',
  courrier_vote_rejete: 'Vote : rejeté',
  courrier_valide: 'Courrier validé (unanimité)',
  courrier_rejete: 'Courrier rejeté',
  courrier_envoye_email: 'Courrier envoyé par email',
  courrier_envoye_postal: 'Courrier envoyé par voie postale',
  courrier_annule: 'Courrier annulé',
  courrier_duplique: 'Courrier dupliqué pour modification',
  
  // Email
  email_recu: 'Email reçu',
  email_archive: 'Email archivé',
  email_analyse_sentinelle: 'Email analysé par Sentinelle',
  email_pj_extraite: 'Pièce jointe extraite vers GED',
  
  // Document
  document_uploade: 'Document uploadé',
  document_supprime: 'Document supprimé',
  document_classifie: 'Document classifié',
  document_analyse: 'Document analysé par Sentinelle',
  
  // Affaire
  affaire_creee: 'Affaire créée',
  affaire_modifiee: 'Affaire modifiée',
  affaire_cloturee: 'Affaire clôturée',
  affaire_rouverte: 'Affaire rouverte',
  
  // Signalement
  signalement_cree: 'Signalement créé',
  signalement_traite: 'Signalement traité',
  signalement_cloture: 'Signalement clôturé',
  
  // Utilisateur
  utilisateur_connecte: 'Connexion',
  utilisateur_deconnecte: 'Déconnexion',
  utilisateur_profil_modifie: 'Profil modifié',
  utilisateur_role_modifie: 'Rôle modifié',
  utilisateur_invite: 'Utilisateur invité',
  utilisateur_active: 'Compte activé',
  utilisateur_desactive: 'Compte désactivé',
  
  // Système
  notification_envoyee: 'Notification envoyée',
  rappel_vote_envoye: 'Rappel de vote envoyé',
  backup_effectue: 'Sauvegarde effectuée',
  erreur_systeme: 'Erreur système'
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Génère une description lisible pour un log
 */
export function genererDescriptionLog(
  action: ActionLog,
  acteurNom: string,
  cibleLibelle?: string
): string {
  const actionDesc = ACTION_LOG_DESCRIPTIONS[action];
  
  if (cibleLibelle) {
    return `${acteurNom} : ${actionDesc} - ${cibleLibelle}`;
  }
  
  return `${acteurNom} : ${actionDesc}`;
}

/**
 * Détermine le niveau d'importance d'une action
 */
export function getNiveauAction(action: ActionLog): NiveauLog {
  // Actions critiques
  const critiques: ActionLog[] = [
    'copropriete_suspendue',
    'courrier_envoye_email',
    'courrier_envoye_postal',
    'utilisateur_desactive',
    'erreur_systeme'
  ];
  
  // Actions importantes
  const importantes: ActionLog[] = [
    'copropriete_creee',
    'copropriete_activee',
    'courrier_valide',
    'courrier_rejete',
    'courrier_vote_rejete',
    'utilisateur_role_modifie'
  ];
  
  if (critiques.includes(action)) return 'critique';
  if (importantes.includes(action)) return 'important';
  return 'info';
}

/**
 * Formate une date de log pour affichage
 */
export function formaterDateLog(date: Timestamp): string {
  const d = date.toDate();
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Filtre les logs par catégorie
 */
export function filtrerParCategorie(logs: Log[], categorie: CategorieLog): Log[] {
  return logs.filter(log => log.categorie === categorie);
}

/**
 * Filtre les logs par niveau minimum
 */
export function filtrerParNiveau(logs: Log[], niveauMin: NiveauLog): Log[] {
  const niveaux: NiveauLog[] = ['info', 'important', 'critique'];
  const indexMin = niveaux.indexOf(niveauMin);
  
  return logs.filter(log => {
    const indexLog = niveaux.indexOf(log.niveau);
    return indexLog >= indexMin;
  });
}